import { useState } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { User, Lock, Trash2 } from 'lucide-react';
import useAuthStore from '../store/authStore';
import DashboardLayout from '../components/DashboardLayout';
import { toast } from 'sonner';
import api from '../lib/api';
import { useSafePostHog } from '../components/PostHogProvider';

const Settings = () => {
  const posthog = useSafePostHog();
  const { user, setUser } = useAuthStore();
  const [profileData, setProfileData] = useState({ full_name: user?.full_name || '', email: user?.email || '' });
  const [passwordData, setPasswordData] = useState({ current_password: '', new_password: '', confirm_password: '' });
  const [loadingProfile, setLoadingProfile] = useState(false);
  const [loadingPassword, setLoadingPassword] = useState(false);

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    setLoadingProfile(true);
    try {
      const response = await api.put('/auth/profile', { full_name: profileData.full_name });
      setUser({ ...user, full_name: response.data.full_name });
      posthog.capture('profile_updated', { action: 'edit_personal_info' });
      toast.success('Profile updated successfully!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update profile');
    } finally {
      setLoadingProfile(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (passwordData.new_password !== passwordData.confirm_password) { toast.error('Passwords do not match'); return; }
    if (passwordData.new_password.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    setLoadingPassword(true);
    try { toast.info('Password change feature coming soon!'); setPasswordData({ current_password: '', new_password: '', confirm_password: '' }); }
    finally { setLoadingPassword(false); }
  };

  const handleDeleteAccount = () => {
    if (!window.confirm('Delete your account? This cannot be undone.')) return;
    if (!window.confirm('This will permanently delete all your data. Are you absolutely sure?')) return;
    toast.info('Account deletion feature coming soon!');
  };

  return (
    <DashboardLayout title="SETTINGS">
      <div className="max-w-3xl space-y-6">
        <div className="bg-white border border-[#1B4332]/8 rounded-xl p-5" data-testid="profile-section">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-9 h-9 bg-[#1B4332]/5 rounded-lg flex items-center justify-center">
              <User className="w-4 h-4 text-[#1B4332]" strokeWidth={2} />
            </div>
            <h2 className="text-base font-extrabold text-[#1B4332]" style={{ fontFamily: 'Nunito, sans-serif' }}>Profile Information</h2>
          </div>
          <form onSubmit={handleUpdateProfile} className="space-y-4">
            <div>
              <Label className="text-[10px] uppercase tracking-widest font-semibold text-[#1B4332]/40">Full Name</Label>
              <Input value={profileData.full_name} onChange={(e) => setProfileData({ ...profileData, full_name: e.target.value })} className="border border-[#1B4332]/10 rounded-lg focus:border-[#1B4332] h-10" required data-testid="full-name-input" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-widest font-semibold text-[#1B4332]/40">Email</Label>
              <Input type="email" value={profileData.email} disabled className="bg-[#1B4332]/3 border border-[#1B4332]/10 rounded-lg" data-testid="email-input" />
              <p className="text-[10px] text-[#1B4332]/40 mt-1 uppercase tracking-wider font-bold">Email cannot be changed</p>
            </div>
            <Button type="submit" disabled={loadingProfile} className="rounded-lg font-semibold bg-[#1B4332] hover:bg-[#2D6A4F] text-white h-9 px-4 text-[13px] transition-all duration-200" data-testid="update-profile-btn">
              {loadingProfile ? 'Updating...' : 'Update Profile'}
            </Button>
          </form>
        </div>

        <div className="bg-white border border-[#1B4332]/8 rounded-xl p-5" data-testid="password-section">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-9 h-9 bg-[#1B4332]/5 rounded-lg flex items-center justify-center">
              <Lock className="w-4 h-4 text-[#1B4332]" strokeWidth={2} />
            </div>
            <h2 className="text-base font-extrabold text-[#1B4332]" style={{ fontFamily: 'Nunito, sans-serif' }}>Change Password</h2>
          </div>
          <form onSubmit={handleChangePassword} className="space-y-4">
            <div>
              <Label className="text-[10px] uppercase tracking-widest font-semibold text-[#1B4332]/40">Current Password</Label>
              <Input type="password" value={passwordData.current_password} onChange={(e) => setPasswordData({ ...passwordData, current_password: e.target.value })} className="border border-[#1B4332]/10 rounded-lg focus:border-[#1B4332] h-10" required data-testid="current-password-input" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-widest font-semibold text-[#1B4332]/40">New Password</Label>
              <Input type="password" value={passwordData.new_password} onChange={(e) => setPasswordData({ ...passwordData, new_password: e.target.value })} className="border border-[#1B4332]/10 rounded-lg focus:border-[#1B4332] h-10" required data-testid="new-password-input" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-widest font-semibold text-[#1B4332]/40">Confirm New Password</Label>
              <Input type="password" value={passwordData.confirm_password} onChange={(e) => setPasswordData({ ...passwordData, confirm_password: e.target.value })} className="border border-[#1B4332]/10 rounded-lg focus:border-[#1B4332] h-10" required data-testid="confirm-password-input" />
            </div>
            <Button type="submit" disabled={loadingPassword} className="rounded-lg font-semibold bg-[#1B4332] hover:bg-[#2D6A4F] text-white h-9 px-4 text-[13px] transition-all duration-200" data-testid="change-password-btn">
              {loadingPassword ? 'Changing...' : 'Change Password'}
            </Button>
          </form>
        </div>

        <div className="bg-white border border-red-200 rounded-xl p-5" data-testid="danger-zone">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-9 h-9 bg-red-50 rounded-lg flex items-center justify-center">
              <Trash2 className="w-4 h-4 text-red-500" strokeWidth={2} />
            </div>
            <h2 className="text-base font-extrabold text-red-600" style={{ fontFamily: 'Nunito, sans-serif' }}>Danger Zone</h2>
          </div>
          <p className="text-sm text-[#1B4332]/60 mb-4">Once you delete your account, there is no going back. Please be certain.</p>
          <Button onClick={handleDeleteAccount} variant="destructive" className="rounded-lg font-semibold bg-red-500 hover:bg-red-600 text-white h-9 px-4 text-[13px] transition-all duration-200" data-testid="delete-account-btn">
            Delete Account
          </Button>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default Settings;
